import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-32 pb-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/"
            data-testid="back-link"
            className="inline-flex items-center gap-2 text-helix-blue hover:text-helix-blue/80 mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>

          <article className="prose prose-lg max-w-none">
            <h1 data-testid="terms-title" className="text-4xl font-bold text-helix-dark mb-2">
              Terms and Conditions
            </h1>
            <p className="text-xl text-gray-700 mb-2">HelixAspire Intelligence Solutions</p>
            <p className="text-gray-500 mb-8">Date: February 2025 | Version: 1.0</p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">1. Acceptance of Terms</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              By accessing this website or engaging the services of HelixAspire ("we," "our," or "us"), you agree to be bound by these Terms and Conditions. If you do not agree, you must not use our website or services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">2. Services</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              HelixAspire provides open-source intelligence (OSINT), risk analysis, and security-related advisory services. All services are provided on a best-effort basis using publicly available and lawfully obtained information.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              We do not guarantee completeness, accuracy, or timeliness of all information due to the dynamic nature of open-source environments.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">3. Use of Website</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              You agree to use this website only for lawful purposes and in a manner that does not:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
              <li>Infringe the rights of others</li>
              <li>Disrupt or impair website functionality</li>
              <li>Introduce malicious or harmful material</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mb-6">
              We reserve the right to restrict or terminate access at our discretion.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">4. Intellectual Property</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              All content on this website, including text, graphics, reports, branding, and methodologies, is the property of HelixAspire unless otherwise stated.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              You may not reproduce, distribute, or exploit any content without prior written consent.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">5. Reliance on Information</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Information provided by HelixAspire is for general informational and risk awareness purposes only. It does not constitute legal, financial, or operational advice.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              Clients are responsible for their own decisions and actions based on the information provided.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">6. Limitation of Liability</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              To the maximum extent permitted by law, HelixAspire is not liable for any loss, damage, or consequential impact arising from:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li>Use of or reliance on our services or reports</li>
              <li>Inaccuracies or omissions in open-source data</li>
              <li>Delays or interruptions in service delivery</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">7. Confidentiality</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We take reasonable steps to protect client confidentiality. Any confidential information shared with us will be handled in accordance with applicable privacy laws and professional standards.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              Clients are responsible for ensuring they have authority to share any information provided to us.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">8. Third-Party Content</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              Our services may reference or rely on third-party sources. We do not control or endorse third-party content and are not responsible for its accuracy or availability.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">9. Fees and Payment</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              Where services are provided on a paid basis, fees, payment terms, and deliverables will be outlined in a separate agreement or proposal.
            </p>
            <p className="text-gray-600 leading-relaxed mb-6">
              Failure to meet payment terms may result in suspension or termination of services.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">10. Termination</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              We reserve the right to suspend or terminate services where:
            </p>
            <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-6">
              <li>Terms are breached</li>
              <li>There is unlawful or unethical use of our services</li>
              <li>Payment obligations are not met</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">11. Governing Law</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              These Terms and Conditions are governed by the laws of New South Wales, Australia. Any disputes will be subject to the jurisdiction of the courts of New South Wales.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">12. Changes to Terms</h2>
            <p className="text-gray-600 leading-relaxed mb-6">
              We may update these Terms and Conditions from time to time. Updates will be published on our website with a revised date. Continued use of our services constitutes acceptance of the updated terms.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-8 mb-4">13. Contact</h2>
            <p className="text-gray-600 leading-relaxed mb-4">
              For any questions regarding these Terms and Conditions, please contact:
            </p>
            <p className="text-gray-600 leading-relaxed mb-2">
              <strong>HelixAspire</strong>
            </p>
            <p className="text-gray-600 leading-relaxed">
              Email: scott.hardman@helixaspire.com.au
            </p>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default TermsAndConditions;
